import axios from 'axios';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';

const isClientSideRendering = windowManipulationService.isBrowserEnvironment();
let als = null;
if (!isClientSideRendering) {
    try {
        // eslint-disable-next-line global-require
        const { AsyncLocalStorage } = require('async_hooks');
        als = new AsyncLocalStorage();
    } catch (e) {
        als = null;
    }
}

const nowMs = () => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now();
    }
    return Date.now();
};

const getContext = () => {
    const store = als ? als.getStore() : null;
    if (store) {
        return store;
    }
    return {
        headers: {},
        perf: null,
        queryParams: {},
        bearerToken: null,
        userAgent: null,
        cookie: '',
    };
};

export const runWithRequestContext = (initialContext, fn) => {
    if (!als) {
        return fn();
    }
    const base = {
        headers: {},
        perf: null,
        queryParams: {},
        bearerToken: null,
        userAgent: null,
        cookie: '',
    };
    const merged = {
        ...base,
        ...(initialContext || {}),
        headers: { ...(base.headers || {}), ...((initialContext && initialContext.headers) || {}) },
        queryParams: { ...(base.queryParams || {}), ...((initialContext && initialContext.queryParams) || {}) },
    };

    return als.run(merged, fn);
};

const axiosInstanceQueryParams = {};
if (isClientSideRendering && ADRIATIC_PARTNER_RESPONSIVE_SCHEME_HOST === `${window.location.protocol}//${window.location.hostname}`) {
    const params = new URLSearchParams(window.location.search);
    const fsSet = params.get('fs_set');
    if (fsSet) {
        axiosInstanceQueryParams.fs_set = fsSet;
    }
}
const apiHostname = isClientSideRendering ? window.location.hostname : ADRIATIC_RESPONSIVE_SCHEME_HOST.replace('https://', '');
const BASE_URL = `https://${apiHostname}/`;
const BASE_API_URL = `https://${apiHostname.replace(/^www\./, 'api.')}/`;
const RSB_SERVICE = `${BASE_URL}rsb-service/`;
let httpsAgent;
if (!isClientSideRendering) {
    // eslint-disable-next-line global-require
    const dns = require('dns');
    // eslint-disable-next-line global-require
    const https = require('https');

    httpsAgent = new https.Agent({
        rejectUnauthorized: false,
        lookup: (hostname, options, callback) => dns.lookup(hostname, { ...options, family: 4 }, callback),
    });
}
const axiosInstance = axios.create({
    timeout: 40000,
    ...(httpsAgent ? { httpsAgent } : {}),
    params: axiosInstanceQueryParams,
});

axiosInstance.interceptors.request.use(
    (config) => {
        config.metadata = { startTime: nowMs() };
        config.headers = axios.AxiosHeaders.from(config.headers);
        if (isClientSideRendering) {
            const token = userData.getTokenFromLocalStorage();
            config.headers.set('Adriatic-Bearer', token || 0);

            return config;
        }
        // Za svaki request inject-amo custom header-e ako postoje, trenutno se koristi za postavljanje X-Forwarded-For.
        const ctx = getContext();
        const headers = ctx?.headers;
        if (headers && typeof headers === 'object') {
            config.headers.set(headers);
        }
        if (ctx?.bearerToken != null) {
            config.headers.set('Adriatic-Bearer', ctx.bearerToken);
        }
        if (ctx?.userAgent != null) {
            config.headers.set('User-Agent', ctx.userAgent);
        }
        if (typeof ctx?.cookie === 'string') {
            config.headers.set('Cookie', ctx.cookie);
        }

        // SSR request-scoped query params (fs_set etc.) za SSR
        const qp = ctx?.queryParams;
        if (qp && typeof qp === 'object' && Object.keys(qp).length > 0) {
            config.params = { ...(config.params || {}), ...qp };
        }

        return config;
    },
    (error) => Promise.reject(error),
);

const recordApiTiming = (config, status) => {
    const start = config?.metadata?.startTime;
    if (start == null) {
        return;
    }
    const { perf } = getContext();
    if (!perf) {
        return;
    }
    const url = config?.url || '';
    const marker = '/rsb-service';
    const markerIndex = url.indexOf(marker);
    perf.addApiCall({
        method: (config?.method || 'GET').toUpperCase(),
        url: markerIndex !== -1 ? url.slice(markerIndex + marker.length) : url,
        status,
        durationMs: Math.round(nowMs() - start),
    });
};
axiosInstance.interceptors.response.use(
    (response) => {
        recordApiTiming(response.config, response.status);

        return response;
    },
    (error) => {
        recordApiTiming(error.config, error.response?.status ?? 408);

        return Promise.reject(error);
    },
);

export const getAdriaticLang = () => {
    // TODO: #55289 Postavljen je defaultni jezik na "en" iako se izravno kroz site ne moze desit da jezik nije postavljen,
    // plan mi je ukloniti ovo nakon testiranja na produkciji
    const defaultLang = 'en';
    return isClientSideRendering && window.AppConfig ? window.AppConfig.lang : defaultLang;
};

const twoLetterISOLanguageCodeParameter = getAdriaticLang();

export const getAdriaticLangForDateFormats = () => {
    const defaultLang = 'enGB';
    const lang = isClientSideRendering && window.AppConfig ? window.AppConfig.lang : defaultLang;
    const langOverrides = {
        en: defaultLang,
        no: 'nn',
    };
    return langOverrides[lang] ?? lang;
};

// Dodatni parametar langFromSSR koji dolazi preko symfony-a (ne šalje se prilikom poziva sa CSR-a).
export const apiFetchData = (endpointPath, urlParam, langFromSSR, queryParams) => {
    const completePath = `${RSB_SERVICE}${endpointPath}${langFromSSR || twoLetterISOLanguageCodeParameter}`;

    return axiosInstance.get(`${completePath}${urlParam || ''}`, {
        params: {
            ...axiosInstanceQueryParams,
            ...(queryParams || {}),
        },
    });
};

export const apiFetchDataWithoutLangISO = (endpointPath, urlParam) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    if (urlParam) {
        return axiosInstance.get(completePath + urlParam);
    }
    return axiosInstance.get(completePath);
};

export const apiFetchDataTwoLetterBefore = (endpointPath, queryParams) => {
    const completePath = `${RSB_SERVICE}${twoLetterISOLanguageCodeParameter}${endpointPath}`;
    return axiosInstance.get(completePath, {
        params: {
            ...axiosInstanceQueryParams,
            ...(queryParams || {}),
        },
    });
};

export const apiFetchDataWithoutRsbService = (endpointPath) => {
    const completePath = `${twoLetterISOLanguageCodeParameter}/${endpointPath}`;
    return axiosInstance.get(completePath);
};

export const apiFetchDataOutOfRSB = (endpointPath) => {
    const completePath = `${BASE_URL}${twoLetterISOLanguageCodeParameter}${endpointPath}`;
    return axiosInstance.get(completePath);
};

export const apiFetchDataRaw = (endpointPath) => axiosInstance.get(endpointPath);

export const apiPostData = (endpointPath, data) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    return axiosInstance.post(completePath, data);
};

export const apiPostIterableData = (endpointPath, data) => {
    const completePath = `${BASE_API_URL}frontend-marketing-tool/${endpointPath}`;
    return axiosInstance.post(completePath, data);
};

export const apiPostDataWithConfig = (endpointPath, data, config) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    return axiosInstance.post(completePath, data, config);
};

// Ova funkcija se koristi za slanje POST zahtjeva u responsiveFrontendBundle.
export const apiPostUserData = (endpointPath, data) => {
    const mainURL = window.location.origin;
    const completePath = `${mainURL}/${twoLetterISOLanguageCodeParameter}/${endpointPath}`;
    return axiosInstance.post(completePath, data);
};

export const apiPostDataWithLangISO = (endpointPath, data) => {
    const completePath = `${RSB_SERVICE}${endpointPath}${twoLetterISOLanguageCodeParameter}`;
    return axiosInstance.post(completePath, data);
};

export const apiDeleteData = (endpointPath) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    return axiosInstance.delete(completePath);
};

export const getCurrentJobId = () => (isClientSideRendering && window.PageConfig ? window.PageConfig.jobVacancyId : 0);
